// ===========================================
// Warn before losing unsaved edits
// ===========================================
//
// One hook, because the alternative is what this repo already had: each editor
// wiring its own `beforeunload`, or — more commonly — not wiring one at all and
// guarding a single Back button instead.
//
// ⚠️ WHAT THIS COVERS, AND WHAT IT CANNOT.
//
//   COVERED: closing the tab, reloading, the browser Back/Forward buttons, and
//   typing a new URL. The browser shows its own generic confirmation.
//
//   NOT COVERED: in-app navigation — clicking a sidebar link. `beforeunload`
//   never fires for that, because the document is never unloaded.
//
// ⚠️ React Router's `useBlocker` is the in-app answer, and it is NOT AVAILABLE
// HERE. It requires a data router; this app mounts `<BrowserRouter>`
// (`main.tsx:46`). Measured, not assumed — calling it throws
// "useBlocker must be used within a data router". Migrating the whole app to
// `createBrowserRouter` is a real change with real risk, and not one to make
// inside a fix for an unsaved-changes prompt.
//
// So in-app navigation stays guarded where each editor already guards it: its
// own Back control, via a confirm dialog. This hook closes the three exits that
// had NO guard at all. Saying which is which matters more than the code — a
// half-covered guard that reads as total is worse than a known gap.

import { useEffect } from 'react';

/**
 * Prompt the browser before unloading the page while `when` is true.
 *
 * @param when Whether there is anything worth losing. Passing `false` removes
 *   the listener entirely, so a clean form never prompts.
 */
export function useUnsavedChangesWarning(when: boolean): void {
  useEffect(() => {
    if (!when) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // ⚠️ BOTH lines are needed. `preventDefault()` is the modern spec;
      // `returnValue` is what Chrome and Safari still read. Modern browsers
      // ignore the string itself and show their own wording, so the text is for
      // older engines only — it is not the message a user will see.
      e.preventDefault();
      e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      return e.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [when]);
}

export default useUnsavedChangesWarning;
